import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StorageIcon from "@mui/icons-material/Storage";
import { DataGrid, GridSortModel } from "@mui/x-data-grid";
import { AuditTypeSummary } from "./audit-types";
import { useAuditRows } from "./use-audit-rows";
import { getAuditColumns } from "./audit-columns";
import { AuditFiltersState } from "./audit-filters-panel";

const DEFAULT_PAGE_SIZE = 25;

interface AuditDatabaseAccordionProps {
  type: AuditTypeSummary;
  databaseName: string;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
  filters: AuditFiltersState;
  sortColumn: string;
  sortDescending: boolean;
  onSortChange: (sortColumn: string, sortDescending: boolean) => void;
}

const AuditDatabaseAccordion = ({
  type,
  databaseName,
  expanded,
  onToggle,
  filters,
  sortColumn,
  sortDescending,
  onSortChange,
}: AuditDatabaseAccordionProps) => {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  useEffect(() => {
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, [filters, sortColumn, sortDescending]);

  const { data, isLoading } = useAuditRows(
    expanded
      ? {
          typeKey: type.typeKey,
          database: databaseName,
          skip: paginationModel.page * paginationModel.pageSize,
          take: paginationModel.pageSize,
          sortColumn,
          sortDescending,
          includeSensitive: filters.includeSensitive,
          dateFrom: filters.dateFrom?.toISOString(),
          dateTo: filters.dateTo?.toISOString(),
          filters: filters.filters,
        }
      : null,
  );

  // Must be referentially stable: the DataGrid treats a new sortModel array as
  // a sort change and resets pagination to page 0.
  const sortModel = useMemo<GridSortModel>(
    () => [{ field: sortColumn, sort: sortDescending ? "desc" : "asc" }],
    [sortColumn, sortDescending],
  );

  const result = data?.databases[databaseName];
  const columns = getAuditColumns(
    type.columns,
    filters.includeSensitive,
    type.dateRangeColumn,
  );

  const handleSortModelChange = (model: GridSortModel) => {
    const sort = model[0];
    if (!sort) return;

    const column = type.columns.find((c) => c.columnName === sort.field);
    if (!column?.isSortable) return;

    onSortChange(sort.field, sort.sort === "desc");
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => onToggle(isExpanded)}
      sx={{
        mb: 2,
        "&:before": { display: "none" },
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        borderRadius: "8px !important",
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ bgcolor: "grey.50", "&:hover": { bgcolor: "grey.100" } }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}
        >
          <StorageIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {databaseName}
          </Typography>
          <Chip
            label={
              result
                ? `${result.totalCount} row${result.totalCount !== 1 ? "s" : ""}`
                : "—"
            }
            color="primary"
            size="small"
            sx={{ ml: "auto", mr: 2, fontWeight: 600 }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {result?.isError ? (
          <Alert severity="warning" sx={{ m: 2 }}>
            Failed to load rows from this database.
          </Alert>
        ) : (
          <Box sx={{ height: 500, width: "100%" }}>
            <DataGrid
              rows={(result?.rows ?? []).map((row, idx) => ({
                id:
                  row[type.primaryKeyColumn] !== undefined
                    ? row[type.primaryKeyColumn]
                    : `${databaseName}-${paginationModel.page * paginationModel.pageSize + idx}`,
                ...row,
              }))}
              columns={columns}
              loading={isLoading}
              paginationMode="server"
              sortingMode="server"
              rowCount={result?.totalCount ?? 0}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50, 100]}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              disableRowSelectionOnClick
              sx={{
                border: "none",
                "& .MuiDataGrid-cell": { borderColor: "grey.200", py: 1 },
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: "grey.50",
                  borderColor: "grey.200",
                },
              }}
            />
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default AuditDatabaseAccordion;
